import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./Admin.css";
import "./Accounts.css";
import { RankedBarChart, rankedSummary } from "./admin/components/charts";
import { KpiCard, StatCard } from "./admin/components/kpis";
import { FilterBar } from "./admin/components/panels";
import { Card, ChartPanel, Notice } from "./admin/components/primitives";
import { count, money, percent, prettyDate, prettyTime } from "./admin/format";
import { useDashboardData, useDashboardPeriod } from "./admin/hooks";
import { usePalette } from "./admin/palette";
import { DashboardSwitcher } from "./admin/components/switcher";
import { ACCOUNTS_DEFAULT_PRESET, ACCOUNTS_PRESETS, trailingYear, withBound } from "./admin/periods";
import { CashFlowChart, IncomeExpenseChart, cashSummary, incomeSummary } from "./accounts/charts";
import {
  BooksCheck,
  LedgerList,
  PositionTable,
  RecentVouchersTable,
  ReportLinks,
  SupplierPayableTable,
  VoucherTypeTable,
  reportLinks,
} from "./accounts/panels";
import { changeFrom, selectAccountsDashboard } from "./accounts/selectors";

const CHART_HEIGHT = { hero: 320, standard: 280 };
const PERIOD_OPTIONS = { presets: ACCOUNTS_PRESETS, defaultPreset: ACCOUNTS_DEFAULT_PRESET };

/** Body of a card's (i) tip: what the figure is, its formula, detail, a worked example and a footnote. */
const HowTip = ({ title, lead, formula, children, example, note }) => (
  <>
    <p className="dash-infotip-title">{title}</p>
    {lead && <p>{lead}</p>}
    {formula && <div className="dash-infotip-formula">{formula}</div>}
    {children}
    {example && (
      <div className="dash-infotip-section">
        <p>
          <strong>Example</strong>
        </p>
        <div className="dash-infotip-formula">{example}</div>
      </div>
    )}
    {note && (
      <div className="dash-infotip-section">
        <p>{note}</p>
      </div>
    )}
  </>
);

const Accounts = () => {
  const basePalette = usePalette();
  // Accounts leads with the Limerick brand navy in place of the default blue.
  const palette = useMemo(() => ({ ...basePalette, sales: basePalette.brand }), [basePalette]);
  const { preset, range, selectPreset, setBound, setRange } = useDashboardPeriod(PERIOD_OPTIONS);
  const { data, error, loading, refreshing, updatedAt, reload } = useDashboardData("/dashboard/accounts", range);
  const view = useMemo(() => selectAccountsDashboard(data, range), [data, range]);

  const { kpi, trend, profit, cash, position, suppliers, vouchers, checks } = view;
  // All time is requested without a start date; once data arrives, the API's
  // resolved range (first transaction on file) is what the page describes.
  const shown = { from: data?.range?.from || range.from, to: data?.range?.to || range.to };
  const periodLabel = shown.from ? `${prettyDate(shown.from)} – ${prettyDate(shown.to)}` : `All time – ${prettyDate(shown.to)}`;
  const period = shown.from ? `from=${shown.from}&to=${shown.to}` : `to=${shown.to}`;
  const noComparison = "All time · no earlier period";

  // Editing a date while on All time starts a custom range from the first transaction.
  const onBound = (field, value) => {
    if (range.from) return setBound(field, value);
    const next = withBound({ from: shown.from || value, to: range.to }, field, value);
    if (next) setRange(next);
  };
  const asOf = position.asOf;
  const jumpTo = trailingYear(view.lastDate);

  const status = loading ? "Loading dashboard…" : refreshing ? "Updating…" : updatedAt ? `Updated ${prettyTime(updatedAt)}` : "";

  const asOfLabel = prettyDate(asOf);
  const openingLabel = position.openingAsOf ? prettyDate(position.openingAsOf) : "the day before the period";
  const changeNote = (
    <>
      <strong>Change %:</strong>{" "}
      {view.allTime
        ? "not shown for All time: there is no earlier period to compare with."
        : `against the previous period of the same length (${view.comparison.replace(/^vs /, "")}). Shown only when that period was positive.`}
    </>
  );
  const positionNote = (
    <>
      <strong>&ldquo;Was&rdquo; and change %:</strong> the same calculation as at {openingLabel}, the day before the period
      starts.
    </>
  );

  const kpis = [
    {
      key: "revenue",
      label: "Revenue",
      value: money(kpi.revenue?.value),
      delta: kpi.revenue?.change_pct,
      color: palette.sales,
      info: profit.fromOrders ? (
        <HowTip
          title="How Revenue is calculated"
          lead={
            <>
              Taken from <strong>primary sales orders</strong> (invoices), not journal vouchers. Same figure as Revenue on the
              Profit &amp; Loss statement.
            </>
          }
          formula={"Per order line:\n  VAT-able value × invoiced qty\n  (or line amount if no VAT-able value)\n\nRevenue = SUM of all lines"}
          example={
            "Order PS1006, 3 lines:\n" +
            "  Soybean Oil 500 ml  10 × ৳83  =   ৳830\n" +
            "  Soybean Oil 1 ltr    5 × ৳163 =   ৳815\n" +
            "  Soybean 2 Ltr        2 × ৳300 =   ৳600\n" +
            "    (VAT-able value ৳300 used, not ৳326)\n" +
            "Revenue = 830 + 815 + 600 = ৳2,245"
          }
          note="Change %: vs the previous period of the same length; hidden when that period had no revenue."
        >
          <p>
            Sales orders dated {periodLabel}; deleted orders are left out. Counts on the order date, even if undelivered or
            unpaid.
          </p>
        </HowTip>
      ) : (
        <HowTip
          title="How Revenue is calculated"
          lead={
            <>
              Taken from the <strong>sales accounts 35–39</strong> (sales less returns and discounts). Same figure as Revenue
              on the Profit &amp; Loss statement.
            </>
          }
          formula={"Revenue = SUM(credit) − SUM(debit)"}
          note="Change %: vs the previous period of the same length; hidden when that period had no revenue."
        >
          <p>Journal vouchers dated {periodLabel}; deleted vouchers are left out.</p>
        </HowTip>
      ),
    },
    {
      key: "expense",
      label: "Expenses",
      value: money(kpi.expenses?.value),
      delta: kpi.expenses?.change_pct,
      color: palette.pending,
      invert: true,
      info: (
        <HowTip
          title="How Expenses is calculated"
          lead={<>The expense lines of the Profit &amp; Loss statement, {periodLabel}:</>}
          formula={"Direct costs (41, 42)\n+ Administration (44)\n+ Salary & remuneration (191, 163)\n+ Selling & marketing (43)\n+ Finance costs + Tax (45, 46)\neach = SUM(debit) − SUM(credit)"}
          note={<>{changeNote} A rise shows red: costs went up.</>}
        >
          <p>
            Each account counts with every account under it; the salary accounts are counted once, on their own line.
            Journal voucher items dated in the period; deleted vouchers are left out. Expense accounts not mapped to a P&amp;L
            line are not in this total; they show in net profit as unclassified.
          </p>
        </HowTip>
      ),
    },
    {
      key: "profit",
      label: "Net profit after tax",
      value: money(kpi.net_profit?.value),
      delta: kpi.net_profit?.change_pct,
      color: palette.collection,
      info: (
        <HowTip
          title="How Net profit after tax is calculated"
          lead={<>The bottom line of the Profit &amp; Loss statement, {periodLabel}:</>}
          formula={"Revenue − Direct costs\n  = Gross profit\n− Admin − Salary − Selling\n  = Operating profit\n+ Other income (40) ± Unclassified\n− Finance costs − Tax (45, 46)\n  = Net profit after tax"}
          note={<>{changeNote} So a swing from profit to loss reads below −100%.</>}
        >
          <p>
            Unclassified = income or expense accounts not mapped to any P&amp;L line, kept in so the result still agrees with
            the ledger.
          </p>
        </HowTip>
      ),
    },
    {
      key: "cash_balance",
      label: "Cash & bank balance",
      value: money(cash.closing),
      delta: changeFrom(cash.closing, cash.opening),
      color: palette.volume,
      caption: `from ${money(cash.opening)} at the start`,
      info: (
        <HowTip
          title="How Cash & bank balance is calculated"
          lead={
            <>
              Balance of account <strong>11 – Cash In Hand And Bank</strong> and every account under it, as at {asOfLabel}:
            </>
          }
          formula={"SUM(opening amount)\n+ SUM(debit) − SUM(credit)"}
          note={
            <>
              <strong>Change %:</strong> against the balance at the start ({money(cash.opening)} on {openingLabel}), shown only
              when that was positive. The sparkline is the running balance through the period.
            </>
          }
        >
          <p>
            Journal voucher items dated on or before {asOfLabel}; deleted vouchers are left out.
            {cash.accounts.length > 0 && (
              <> Made up of {cash.accounts.map((account) => `${account.name} ${money(account.value)}`).join(" and ")}.</>
            )}
          </p>
        </HowTip>
      ),
    },
  ];

  const stats = [
    {
      key: "margin",
      label: "Gross margin",
      value: profit.grossMargin === null ? "–" : percent(profit.grossMargin),
      caption: `${money(profit.grossProfit)} gross profit${profit.netMargin === null ? "" : ` · net ${percent(profit.netMargin)}`}`,
      info: (
        <HowTip
          title="How Gross margin is calculated"
          lead={<>From the Profit &amp; Loss statement, {periodLabel}:</>}
          formula={"Gross profit = Revenue − Direct costs (41, 42)\nGross margin = Gross profit ÷ Revenue × 100"}
          note="Shown as – when revenue is zero or negative, where a margin has no meaning."
        >
          <p>
            Direct costs are Cost Of Goods Sold and Cost Of Goods Sold (FOH), with every account under them. The caption&rsquo;s
            net margin = Net profit after tax ÷ Revenue × 100.
          </p>
        </HowTip>
      ),
    },
    {
      key: "net_cash",
      label: "Net cash flow",
      value: money(kpi.net_cash?.value),
      delta: kpi.net_cash?.change_pct,
      caption: `${money(kpi.cash_in?.value)} in, ${money(kpi.cash_out?.value)} out`,
      info: (
        <>
          <p className="dash-infotip-title">How Net cash flow is calculated</p>
          <p>
            Movement through account <strong>11 – Cash In Hand And Bank</strong> and every account under it (cash in hand
            and all banks), {periodLabel}:
          </p>
          <div className="dash-infotip-formula">Net cash flow = Cash in − Cash out</div>
          <p>
            Takes every voucher with a cash or bank line in the period and reads its other lines: a <strong>credit</strong>{" "}
            there is cash <strong>in</strong>, a <strong>debit</strong> is cash <strong>out</strong>. Transfers between cash
            and bank accounts are left out; deleted vouchers too.
          </p>
          <p>
            Always equals closing cash − opening cash ({money(cash.closing)} − {money(cash.opening)}). Matches the Cash Flow
            Statement for the same dates.
          </p>
          <div className="dash-infotip-section">
            <p>
              <strong>Change %:</strong> against the previous period of the same length. Shown only when that period was
              positive; a swing from positive to negative reads below −100%.
            </p>
          </div>
        </>
      ),
    },
    {
      key: "receivable",
      label: "Trade receivable",
      hint: `At ${prettyDate(asOf)}`,
      value: money(position.receivable.value),
      caption: `was ${money(position.receivable.opening)} at the start`,
      info: (
        <>
          <p className="dash-infotip-title">How Trade receivable is calculated</p>
          <p>
            Account <strong>12 – Trade Receivable</strong> and every account under it (Local, Export and Intercompany Sales
            and their sub-accounts), as at {prettyDate(asOf)}:
          </p>
          <div className="dash-infotip-formula">SUM(opening amount){"\n"}+ SUM(debit) − SUM(credit)</div>
          <p>
            Postings sit on the lowest-level accounts; each total rolls up into its parent, so account 12 holds the whole
            tree. Counts journal voucher items dated on or before {prettyDate(asOf)}; deleted vouchers are left out. Matches
            the Trade Receivable line on the Balance Sheet.
          </p>
          <div className="dash-infotip-section">
            <p>
              <strong>&ldquo;At the start&rdquo;:</strong> the same calculation up to{" "}
              {position.openingAsOf ? prettyDate(position.openingAsOf) : "the day before the period"}. The difference is the
              period&rsquo;s net change: invoiced less collected.
            </p>
          </div>
        </>
      ),
    },
    {
      key: "payable",
      label: "Trade payable",
      hint: `At ${prettyDate(asOf)}`,
      value: money(position.payable.value),
      caption: `${count(suppliers.payableCount)} supplier(s) owed ${money(suppliers.payableTotal)}`,
      info: (
        <>
          <p className="dash-infotip-title">How Trade payable is calculated</p>
          <p>
            Account <strong>22 – Trade Creditors And Others Payable</strong> and every account under it (children, their
            children and so on), as at {prettyDate(asOf)}:
          </p>
          <div className="dash-infotip-formula">SUM(opening amount){"\n"}+ SUM(credit) − SUM(debit)</div>
          <p>
            Counts journal voucher items dated on or before {prettyDate(asOf)}; deleted vouchers are left out. Matches the
            Trade Payable line on the Balance Sheet.
          </p>
          <div className="dash-infotip-section">
            <p>
              <strong>Supplier line:</strong> per supplier on voucher items, credit − debit up to the same date. Counts
              suppliers owed more than zero and totals what they are owed; covers all accounts, no opening amount, so it can
              differ from the figure above.
            </p>
          </div>
        </>
      ),
    },
  ];

  const positionStats = [
    {
      key: "assets",
      label: "Total assets",
      value: money(position.assets.value),
      delta: changeFrom(position.assets.value, position.assets.opening),
      caption: `was ${money(position.assets.opening)}`,
      info: (
        <HowTip
          title="How Total assets is calculated"
          lead={<>Total assets on the Balance Sheet as at {asOfLabel}: every Asset account (Current and Non-Current Assets and every account under them):</>}
          formula={"SUM(opening amount)\n+ SUM(debit) − SUM(credit)"}
          note={positionNote}
        >
          <p>
            Journal voucher items dated on or before {asOfLabel}; deleted vouchers are left out. Sub-account totals roll up
            into their parents.
          </p>
        </HowTip>
      ),
    },
    {
      key: "liabilities",
      label: "Total liabilities",
      value: money(position.liabilities.value),
      delta: changeFrom(position.liabilities.value, position.liabilities.opening),
      caption: `was ${money(position.liabilities.opening)}`,
      info: (
        <HowTip
          title="How Total liabilities is calculated"
          lead={<>Total liabilities on the Balance Sheet as at {asOfLabel}: every Liability account (Current and Non-Current Liabilities and every account under them):</>}
          formula={"SUM(opening amount)\n+ SUM(credit) − SUM(debit)"}
          note={positionNote}
        >
          <p>
            Journal voucher items dated on or before {asOfLabel}; deleted vouchers are left out. Read credit-positive, so a
            positive figure is owed.
          </p>
        </HowTip>
      ),
    },
    {
      key: "equity",
      label: "Equity",
      hint: "incl. unclosed profit",
      value: money(position.equity.value),
      delta: changeFrom(position.equity.value, position.equity.opening),
      caption: `was ${money(position.equity.opening)}`,
      info: (
        <HowTip
          title="How Equity is calculated"
          lead={<>Equity on the Balance Sheet as at {asOfLabel}: share capital, premium, reserves, retained earnings and the P&amp;L account, plus profit not yet closed to equity:</>}
          formula={"Equity accounts:\n  SUM(opening) + SUM(credit) − SUM(debit)\n+ all income − all expenses\n  up to " + asOfLabel}
          note={positionNote}
        >
          <p>
            The profit part is cumulative: every income and expense posting up to the date that has not been transferred to
            retained earnings, so the Balance Sheet balances. It is not just this period&rsquo;s profit.
          </p>
        </HowTip>
      ),
    },
    {
      key: "suppliers",
      label: "Supplier advances",
      value: money(suppliers.advanceTotal),
      caption: `${count(suppliers.advanceCount)} supplier(s) paid ahead of billing`,
      info: (
        <HowTip
          title="How Supplier advances is calculated"
          lead={<>From the Supplier Advance report, as at {asOfLabel}:</>}
          formula={"Per supplier: SUM(debit) − SUM(credit)\nTotal = SUM of balances above zero"}
        >
          <p>
            Journal voucher items tagged with a supplier, dated on or before {asOfLabel}, on all accounts; deleted vouchers and
            suppliers are left out, and there is no opening amount. A positive balance means the supplier was paid more than
            they have billed.
          </p>
        </HowTip>
      ),
    },
  ];

  const profitLadder = [
    { name: "Revenue", value: profit.revenue, note: profit.fromOrders ? "from sales invoices" : undefined },
    { name: "Gross profit", value: profit.grossProfit, strong: true },
    { name: "Operating profit", value: profit.operatingProfit, strong: true },
    { name: "Other income", value: profit.otherIncome },
    { name: "Net profit after tax", value: profit.netProfit, strong: true },
  ];

  const workingCapital = [
    { key: "receivable", label: "Trade receivable", ...position.receivable },
    { key: "advances", label: "Advances & prepayments", ...position.advances },
    { key: "inventory", label: "Inventory", ...position.inventory },
    { key: "payable", label: "Trade payable", ...position.payable },
  ];

  const cashLines = [
    ...cash.activities.map((activity) => ({
      name: activity.label.replace("Cash Flows from ", ""),
      value: activity.net,
      note: `${money(activity.inflow)} in · ${money(activity.outflow)} out`,
    })),
    { name: "Net cash flow", value: kpi.net_cash?.value, strong: true },
  ];

  const balanceLines = [
    ...cash.accounts.map((account) => ({ name: account.name, value: account.value })),
    { name: "Closing cash & bank", value: cash.closing, strong: true },
  ];

  return (
    <div className="dash dash--brand">
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Accounts Dashboard</h5>
        </div>

        <div className="am-pagebody">
          <header className="dash-header">
            <div>
              <h1 className="dash-title">Accounts overview</h1>
              <p className="dash-subtitle">
                {periodLabel}
                {view.comparison && <span className="dash-muted"> · compared {view.comparison}</span>}
                {view.allTime && <span className="dash-muted"> · since the first transaction on file</span>}
              </p>
            </div>
            <div className="dash-header-actions">
              <DashboardSwitcher current="accounts" />
              <span className="dash-status" role="status" aria-live="polite">
                {status}
              </span>
              <Link className="dash-btn dash-btn--primary" to="/create-journal-voucher">
                <i className="fa fa-plus" aria-hidden="true" /> New voucher
              </Link>
              <button type="button" className="dash-btn" disabled={loading || refreshing} onClick={reload}>
                <i className={`fa fa-refresh${refreshing ? " fa-spin" : ""}`} aria-hidden="true" /> Refresh
              </button>
            </div>
          </header>

          <FilterBar preset={preset} range={range} presets={ACCOUNTS_PRESETS} onPreset={selectPreset} onBound={onBound} />

          {error && (
            <Notice actionLabel="Retry" onAction={reload}>
              {error}
            </Notice>
          )}

          {view.outsideData && jumpTo && (
            <Notice actionLabel="Show last 12 months of activity" onAction={() => setRange(jumpTo)}>
              No voucher was posted between {periodLabel}. The latest voucher on file is dated {prettyDate(view.lastDate)}.
            </Notice>
          )}

          {!loading && checks.issues.length > 0 && (
            <Notice>
              The books need attention: {checks.issues.length} check(s) failed. See <strong>Books check</strong> below.
            </Notice>
          )}

          <div className={`dash-body${refreshing ? " is-refetching" : ""}`} aria-busy={loading || refreshing}>
            {/* Report shortcuts, opened on the period picked above */}
            <div className="dash-grid">
              <Card
                title="Reports"
                subtitle={`Open on ${periodLabel}`}
                action={
                  <Link className="dash-btn" to="/reports">
                    All reports
                  </Link>
                }
              >
                <ReportLinks links={reportLinks(shown)} />
              </Card>
            </div>

            {/* Headline KPIs */}
            <div className="dash-grid dash-grid--4">
              {kpis.map(({ key, caption, ...tile }) => (
                <KpiCard key={key} {...tile} caption={caption || (view.allTime ? noComparison : view.comparison)} series={trend} seriesKey={key} loading={loading} />
              ))}
            </div>

            <div className="dash-grid dash-grid--4">
              {stats.map(({ key, ...stat }) => (
                <StatCard key={key} {...stat} loading={loading} />
              ))}
            </div>

            {/* Profit & loss */}
            <div className="dash-section">
              <h2 className="dash-section-title">Profit &amp; loss</h2>
              <p className="dash-section-sub">
                Figures match the Profit &amp; Loss statement for the same period
                {profit.fromOrders && "; revenue is the VAT-able value of sales invoices"}.
              </p>
            </div>

            <div className="dash-grid dash-grid--8-4">
              <div className="dash-cell">
                <Card title="Income vs expenses" subtitle={`By ${view.granularity} · ${periodLabel}`}>
                  <ChartPanel loading={loading} empty={trend.length < 1} height={CHART_HEIGHT.hero} summary={incomeSummary(trend)}>
                    <IncomeExpenseChart data={trend} palette={palette} height={CHART_HEIGHT.hero} />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card
                  title="Profit ladder"
                  subtitle="From revenue down to the bottom line"
                  action={
                    <Link className="dash-btn" to={`/profit-and-loss?${period}`}>
                      P&amp;L
                    </Link>
                  }
                >
                  <LedgerList rows={profitLadder} loading={loading} />
                </Card>
              </div>
            </div>

            <div className="dash-grid dash-grid--7-5">
              <div className="dash-cell">
                <Card title="Where the money went" subtitle="Expenses by P&L line over the selected period">
                  <ChartPanel
                    loading={loading}
                    empty={profit.expenseLines.length === 0}
                    emptyText="No net expense was posted in this period."
                    height={CHART_HEIGHT.standard}
                    summary={rankedSummary(profit.expenseLines, profit.expenseTotal)}
                  >
                    <RankedBarChart
                      data={profit.expenseLines}
                      total={profit.expenseTotal}
                      palette={palette}
                      color={palette.pending}
                      seriesName="Expense"
                      height={CHART_HEIGHT.standard}
                      labelWidth={190}
                      labelChars={28}
                    />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card
                  title="Vouchers posted"
                  subtitle={`${count(vouchers.count)} in this period`}
                  action={
                    <Link className="dash-btn" to="/journal-voucher">
                      All vouchers
                    </Link>
                  }
                  flush
                >
                  <VoucherTypeTable rows={vouchers.byType} loading={loading} />
                </Card>
              </div>
            </div>

            {/* Cash */}
            <div className="dash-section">
              <h2 className="dash-section-title">Cash &amp; bank</h2>
              <p className="dash-section-sub">
                Money received and paid through cash and bank accounts; transfers between them are left out.
              </p>
            </div>

            <div className="dash-grid dash-grid--8-4">
              <div className="dash-cell">
                <Card title="Cash in vs cash out" subtitle={`By ${view.granularity} · ${periodLabel}`}>
                  <ChartPanel
                    loading={loading}
                    empty={!cash.hasMovement}
                    emptyText="No cash or bank movement in this period."
                    height={CHART_HEIGHT.standard}
                    summary={cashSummary(trend)}
                  >
                    <CashFlowChart data={trend} palette={palette} height={CHART_HEIGHT.standard} />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card
                  title="Cash by activity"
                  subtitle="Net flow per activity"
                  action={
                    <Link className="dash-btn" to={`/cash-flow-statement?${period}`}>
                      Cash flow
                    </Link>
                  }
                >
                  <LedgerList rows={cashLines} loading={loading} />
                  <div className="mg-t-15">
                    <LedgerList rows={balanceLines} loading={loading} />
                  </div>
                </Card>
              </div>
            </div>

            {/* Financial position */}
            <div className="dash-section">
              <h2 className="dash-section-title">Financial position</h2>
              <p className="dash-section-sub">
                As at {prettyDate(asOf)}, compared with {position.openingAsOf ? prettyDate(position.openingAsOf) : "the start of the period"}.
              </p>
            </div>

            <div className="dash-grid dash-grid--4">
              {positionStats.map(({ key, ...stat }) => (
                <StatCard key={key} {...stat} loading={loading} />
              ))}
            </div>

            <div className="dash-grid dash-grid--7-5">
              <div className="dash-cell">
                <Card
                  title="Working capital"
                  subtitle="Balances that turn into cash, or out of it"
                  action={
                    <Link className="dash-btn" to={`/statement-of-financial-position?as_of=${asOf}`}>
                      Balance sheet
                    </Link>
                  }
                  flush
                >
                  <PositionTable rows={workingCapital} openingAsOf={position.openingAsOf} asOf={asOf} loading={loading} />
                </Card>
              </div>

              <div className="dash-cell">
                <Card title="Books check" subtitle="Does everything add up?">
                  <BooksCheck checks={checks} asOf={asOf} range={shown} loading={loading} />
                </Card>
              </div>
            </div>

            {/* Payables and vouchers */}
            <div className="dash-grid dash-grid--halves">
              <div className="dash-cell">
                <Card
                  title="Largest supplier payables"
                  subtitle={`Owed at ${prettyDate(asOf)}`}
                  action={
                    <Link className="dash-btn" to={`/supplier-payable?as_of=${asOf}`}>
                      All payables
                    </Link>
                  }
                  flush
                >
                  <SupplierPayableTable rows={suppliers.top} asOf={asOf} loading={loading} />
                </Card>
              </div>

              <div className="dash-cell">
                <Card
                  title="Latest vouchers"
                  subtitle="Most recent entries, regardless of period"
                  action={
                    <Link className="dash-btn" to="/journal-voucher">
                      All vouchers
                    </Link>
                  }
                  flush
                >
                  <RecentVouchersTable rows={vouchers.recent} loading={loading} />
                </Card>
              </div>
            </div>

          </div>
        </div>

        <footer className="am-footer">
          <span>
            &copy; {new Date().getFullYear()}{" "}
            <a href="http://limerickbd.com/" target="_blank" rel="noopener noreferrer">
              Limerick Resources Limited
            </a>
            . All rights reserved.
          </span>
        </footer>
      </div>
    </div>
  );
};

export default Accounts;
